import React, { FC, ChangeEvent, useState, useRef, useEffect } from 'react';
import FormAddressInput, { FormAddressInputProps } from './FormAddressInput';
import { FormAddressItemProps } from './FormAddressItem';
import './css/form-address-autocomplete.css';

interface Props {
  request?: (search: string, signal: AbortSignal, cleanFn: () => void) => Promise<JSX.Element[]>;
}

const autoCompleteTimeouts: Record<string, NodeJS.Timeout|undefined> = {};
const autoCompleteAborts: Record<string, AbortController|undefined> = {};

const FormAddressAutocomplete: FC<Props & FormAddressInputProps & FormAddressItemProps> = (props): JSX.Element => {
  const [timeoutKey] = useState<string>(`fa-autocomplete-${new Date().getTime()}`);
  const [items, setItems] = useState<JSX.Element[]>([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState<boolean>(false);
  const [showList, setShowList] = useState<boolean>(false);
  const [canClose, setCanClose] = useState<boolean>(true);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fnResize = () => {
      if (ref.current) {
        const { innerWidth } = window;
        const box = ref.current.getBoundingClientRect();
        ref.current.style.setProperty('--fa-autocomplete-max-width', `${innerWidth - box.left - 16}px`);
      }
    };
    fnResize();
    window.addEventListener('resize', fnResize);
    return () => window.removeEventListener('resize', fnResize);
  },[ref.current]);

  const cleanPropsForInput = (): FormAddressInputProps => {
    const cloneProps = { ...props };
    delete cloneProps.request;
    return cloneProps;
  };

  const handleFocus = () => {
    items.length > 0 && setShowList(true);
  };

  const handleBlur = () => {
    canClose && showList && setShowList(false);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    items.length > 0 && setItems([]);
    clearTimeout(autoCompleteTimeouts[timeoutKey]);
    autoCompleteAborts[timeoutKey]?.abort();
    props.onChange?.(event);
    const { target } = event;
    if (target instanceof HTMLInputElement && props.request) {
      autoCompleteTimeouts[timeoutKey] = setTimeout(() => {
        setAutocompleteLoading(true);
        const abortController = new AbortController();
        autoCompleteAborts[timeoutKey] = abortController;
        props.request?.(target.value, abortController.signal, () => {
          setItems([]);
          setShowList(false);
          setCanClose(true);
        })
          .then((elements) => {
            setItems(elements);
            elements.length > 0 && setShowList(true);
          })
          .finally(() => setAutocompleteLoading(false));
      }, 150);
    }
  };

  return (
    <div className="fa-autocomplete-container" ref={ref}>
      <FormAddressInput {...cleanPropsForInput()} loading={autocompleteLoading || props.loading} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} />
      {showList && items.length > 0 && (
        <div
          className="fa-autocomplete-list"
          onMouseEnter={() => setCanClose(false)}
          onMouseLeave={() => setCanClose(true)}
          onMouseDown={() => setCanClose(false)}
          onMouseUp={() => setCanClose(true)}
          onClick={() => setShowList(false)}
        >
          {items}
        </div>
      )}
    </div>
  );
};

export default FormAddressAutocomplete;